import React, { useCallback, useEffect, useState } from 'react';

import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Card, Divider, Pagination, Space, Spin, Table, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';

import { toast } from 'react-toastify';
import api from '~/services/api';
import { Container } from './styles';

export default function RamsListByPN({ match }) {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationTotalItems, setPaginationTotalItems] = useState(0);
  const [paginationSize, setPaginationSize] = useState(25);
  const [paginationPage, setPaginationPage] = useState(1);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [sortField, setSortField] = useState('');

  const { Title, Text, Paragraph } = Typography;

  const handleSearch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('size', paginationSize);
      params.append('page', paginationPage - 1); // Backend expects 0-based page index

      if (sortField && sortOrder) {
        params.append('sortField', sortField);
        params.append('order', sortOrder === 'ascend' ? 'ASC' : 'DESC');
      }

      const { data: itemInventoryData } = await api.get(`inventory_rams/${match.params.item_id}?${params}`);

      setInventoryItems(itemInventoryData);
      setPaginationTotalItems(itemInventoryData.totalItems || 0);
      setPaginationSize(itemInventoryData.sizePage || 25);
      setPaginationPage((itemInventoryData.currentPage || 0) + 1);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error loading data');
    } finally {
      setLoading(false);
    }
  }, [match.params.item_id, paginationPage, paginationSize, sortField, sortOrder]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  const handleTableChange = (pagination, filters, sorter) => {
    const newSortOrder = sorter.order;
    const newSortField = Array.isArray(sorter.field) ? sorter.field.join('.') : sorter.field;

    setSortOrder(newSortOrder);
    setSortField(newSortField);

    // Reset to page 1 when sorting changes
    if (newSortOrder !== sortOrder || newSortField !== sortField) {
      setPaginationPage(1);
    }
  };

  const handlePaginationChange = (page, pageSize) => {
    if (page !== paginationPage || pageSize !== paginationSize) {
      setPaginationPage(page);
      setPaginationSize(pageSize);
    }
  };

  const columns = [
    {
      title: 'Serial Number',
      dataIndex: 'serial_number',
      key: 'serial_number',
      sorter: true,
      render: (text) => (
        <Paragraph style={{ margin: 0, padding: 0 }} copyable>
          {text}
        </Paragraph>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status) => <Tag color={status === 'InStock' ? 'green' : status === 'ToBeInspected' ? 'orange' : 'red'}>{status}</Tag>,
    },
    {
      title: 'Condition',
      dataIndex: 'condition',
      key: 'condition',
      sorter: true,
    },
    {
      title: 'Location',
      dataIndex: ['locations', 'name'],
      key: 'location',
      render: (_text, record) => `${record.locations?.name || ''} - ${record.locations?.description || ''}`,
    },
  ];

  return (
    <Container>
      <div style={{ marginBottom: 16 }}>
        <Link to="/inventoryRams">
          <Button icon={<ArrowLeftOutlined />} type="primary">
            Back to RAM Inventory
          </Button>
        </Link>
      </div>

      {inventoryItems.item?.part_number && (
        <Card
          style={{
            marginBottom: 8,
            borderRadius: 8,
          }}
        >
          <Title level={4} style={{ marginBottom: 16 }}>
            {inventoryItems.item?.brand?.name || inventoryItems.item?.brand || 'Unknown Brand'} -{' '}
            <Text copyable={{ text: inventoryItems.item?.part_number }}>{inventoryItems.item?.part_number}</Text>
          </Title>
          <Space size="large" split={<Divider type="vertical" />}>
            <Text>
              <Text type="secondary">Capacity:</Text> <Text strong>{inventoryItems.item?.ram?.capacity || 'N/A'}</Text>
            </Text>
            <Text>
              <Text type="secondary">Type:</Text> <Text strong>{inventoryItems.item?.ram?.type || 'N/A'}</Text>
            </Text>
            <Text>
              <Text type="secondary">Total Items:</Text> <Text strong>{paginationTotalItems}</Text>
            </Text>
          </Space>
          {inventoryItems.item?.description && (
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">{inventoryItems.item?.description}</Text>
            </div>
          )}
        </Card>
      )}

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            pagination={false}
            dataSource={inventoryItems.rows}
            scroll={{ x: 1500, y: 'calc(100vh - 28em)' }}
            rowKey="id"
            size="small"
            onChange={handleTableChange}
          />
        </Spin>

        <Pagination
          current={paginationPage}
          pageSize={paginationSize}
          total={paginationTotalItems}
          onChange={handlePaginationChange}
          showQuickJumper
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
        />
      </Space>
    </Container>
  );
}
